import PrescriptionPrintLayout, {
  PrintHospital,
  PrintDoctor,
  PrintPatient,
  PrintVitals,
} from './PrescriptionPrintLayout';

// ── Types ──────────────────────────────────────────────────────────

export interface RxItem {
  drug_name:     string;
  dose:          string;
  frequency:     string;
  duration:      string;
  quantity?:     number;
  instructions?: string;
}

export interface DigitalRxEncounter {
  id:                  string;
  encounter_no?:       string;
  encounter_date?:     string;
  chief_complaint?:    string;
  soap_assessment?:    string;
  soap_plan?:          string;
  primary_icd10_code?: string;
  primary_icd10_desc?: string;
  follow_up_date?:     string;
  referral_to?:        string;
}

export interface DigitalRxPrescription {
  items:                RxItem[];
  general_instructions?: string;
}

export interface DigitalRxData {
  hospital:     PrintHospital;
  doctor:       PrintDoctor;
  patient:      PrintPatient & { allergies?: string[] };
  encounter:    DigitalRxEncounter;
  vitals?:      PrintVitals;
  prescription: DigitalRxPrescription | null;
}

interface Props {
  data:   DigitalRxData;
  onDone: () => void;
}

// ── Section label ──────────────────────────────────────────────────

function SectionLabel({ text }: { text: string }) {
  return (
    <div style={{
      fontSize:      '6pt',
      fontWeight:    700,
      color:         '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      marginBottom:  '3px',
      paddingBottom: '2px',
      borderBottom:  '1px solid #f3f4f6',
    }}>
      {text}
    </div>
  );
}

// ── Rx Table ───────────────────────────────────────────────────────

function RxTable({ items }: { items: RxItem[] }) {
  if (!items.length) {
    return (
      <div style={{ fontSize: '8pt', color: '#9ca3af', fontStyle: 'italic' }}>
        No medications prescribed.
      </div>
    );
  }

  return (
    <table style={{
      width:          '100%',
      borderCollapse: 'collapse',
      fontSize:       '8pt',
      color:          '#1f2937',
    }}>
      <thead>
        <tr style={{ background: '#f9fafb', borderBottom: '1.5px solid #e5e7eb' }}>
          {['#', 'Medicine', 'Dose', 'Frequency', 'Duration', 'Instructions'].map(h => (
            <th
              key={h}
              style={{
                textAlign:     'left',
                padding:       '3px 6px',
                fontSize:      '6pt',
                fontWeight:    700,
                color:         '#6b7280',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map((item, idx) => (
          <tr
            key={idx}
            style={{
              borderBottom:    '1px solid #f3f4f6',
              backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa',
            }}
          >
            <td style={{ padding: '3px 6px', color: '#9ca3af', fontFamily: 'monospace' }}>
              {idx + 1}
            </td>
            <td style={{ padding: '3px 6px', fontWeight: 600 }}>
              {item.drug_name}
            </td>
            <td style={{ padding: '3px 6px', fontFamily: 'monospace' }}>
              {item.dose || '—'}
            </td>
            <td style={{ padding: '3px 6px' }}>
              {item.frequency || '—'}
            </td>
            <td style={{ padding: '3px 6px' }}>
              {item.duration || '—'}
            </td>
            <td style={{ padding: '3px 6px', color: '#6b7280', fontSize: '7pt' }}>
              {item.instructions || '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Clinical content ───────────────────────────────────────────────

function ClinicalPayload({ encounter, prescription, allergies }: {
  encounter:    DigitalRxEncounter;
  prescription: DigitalRxPrescription | null;
  allergies:    string[];
}) {
  const diagnosisLine = [
    encounter.primary_icd10_code,
    encounter.primary_icd10_desc,
  ].filter(Boolean).join(' — ');

  const assessmentText = encounter.soap_assessment?.trim();
  const planText       = encounter.soap_plan?.trim();

  return (
    <div style={{
      display:        'flex',
      flexDirection:  'column',
      gap:            '6px',
      overflow:       'hidden',
      paddingTop:     '2px',
    }}>

      {/* Chief complaint + allergies */}
      {(encounter.chief_complaint || allergies.length > 0) && (
        <div style={{ flexShrink: 0 }}>
          {encounter.chief_complaint && (
            <div style={{ fontSize: '8pt', color: '#374151', marginBottom: '2px' }}>
              <span style={{ fontWeight: 600 }}>Chief Complaint: </span>
              {encounter.chief_complaint}
            </div>
          )}
          {allergies.length > 0 && (
            <div style={{
              fontSize:     '7pt',
              color:        '#b45309',
              background:   '#fef3c7',
              border:       '1px solid #fde68a',
              borderRadius: '2px',
              padding:      '2px 5px',
              display:      'inline-block',
            }}>
              Known allergies: {allergies.join(', ')}
            </div>
          )}
        </div>
      )}

      {/* Diagnosis */}
      {(diagnosisLine || assessmentText) && (
        <div style={{ flexShrink: 0 }}>
          <SectionLabel text="Diagnosis"/>
          {diagnosisLine && (
            <div style={{ fontSize: '8.5pt', fontWeight: 700, color: '#111827', marginBottom: '1px' }}>
              {diagnosisLine}
            </div>
          )}
          {assessmentText && (
            <div style={{ fontSize: '8pt', color: '#374151', whiteSpace: 'pre-wrap' }}>
              {assessmentText}
            </div>
          )}
        </div>
      )}

      {/* Rx */}
      <div style={{ flexShrink: 0 }}>
        <div style={{
          display:      'flex',
          alignItems:   'baseline',
          gap:          '6px',
          marginBottom: '3px',
        }}>
          <span style={{
            fontFamily: 'Georgia, serif',
            fontWeight: 700,
            fontSize:   '16pt',
            color:      '#374151',
            lineHeight: 1,
          }}>
            Rx
          </span>
          <SectionLabel text="Medications"/>
        </div>
        <RxTable items={prescription?.items ?? []}/>
      </div>

      {/* General instructions */}
      {prescription?.general_instructions && (
        <div style={{ flexShrink: 0, fontSize: '7.5pt', color: '#374151' }}>
          <span style={{ fontWeight: 600 }}>General Instructions: </span>
          {prescription.general_instructions}
        </div>
      )}

      {/* Plan / Advice */}
      {planText && (
        <div style={{ flexShrink: 0 }}>
          <SectionLabel text="Advice / Plan"/>
          <div style={{ fontSize: '8pt', color: '#374151', whiteSpace: 'pre-wrap' }}>
            {planText}
          </div>
        </div>
      )}

      {/* Follow-up / Referral */}
      {(encounter.follow_up_date || encounter.referral_to) && (
        <div style={{
          flexShrink:    0,
          fontSize:      '7.5pt',
          color:         '#374151',
          display:       'flex',
          gap:           '16px',
          flexWrap:      'wrap',
          paddingTop:    '3px',
          borderTop:     '1px dashed #e5e7eb',
        }}>
          {encounter.follow_up_date && (
            <span>
              <span style={{ fontWeight: 600 }}>Follow-up: </span>
              {encounter.follow_up_date}
            </span>
          )}
          {encounter.referral_to && (
            <span>
              <span style={{ fontWeight: 600 }}>Refer to: </span>
              {encounter.referral_to}
            </span>
          )}
        </div>
      )}

      {/* Spacer pushes signature to bottom */}
      <div style={{ flex: 1 }}/>

      {/* Signature block */}
      <div style={{
        flexShrink:  0,
        alignSelf:   'flex-end',
        textAlign:   'center',
        minWidth:    '50mm',
        paddingTop:  '10mm',
        borderTop:   '1px solid #374151',
        marginTop:   '4px',
      }}>
        <div style={{ fontSize: '7pt', color: '#374151', fontWeight: 600 }}>
          Doctor's Signature
        </div>
        <div style={{ fontSize: '6.5pt', color: '#6b7280', marginTop: '2px' }}>
          Stamp / Seal
        </div>
      </div>

    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────

export default function DigitalPrescriptionPrint({ data, onDone }: Props) {
  return (
    <PrescriptionPrintLayout
      hospital={data.hospital}
      doctor={data.doctor}
      patient={data.patient}
      encounterId={data.encounter.id}
      date={data.encounter.encounter_date}
      vitals={data.vitals}
      onDone={onDone}
    >
      <ClinicalPayload
        encounter={data.encounter}
        prescription={data.prescription}
        allergies={data.patient.allergies ?? []}
      />
    </PrescriptionPrintLayout>
  );
}
